import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@fontsource/fraunces/400.css';
import '@fontsource/fraunces/500.css';
import '@fontsource/fraunces/600.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/400-italic.css';
import '@fontsource/hanken-grotesk/400.css';
import '@fontsource/hanken-grotesk/500.css';
import '@fontsource/hanken-grotesk/600.css';
import type { ReportSummaryModelV1 } from '../../../lib/reportSummary/types';
import { hashReportSummaryModel, reportSummaryModelV1Schema } from '../../../lib/reportSummary/types';
import { buildNativePrintStylesheet } from '../../../lib/nativePrint/buildNativePrintStylesheet';
import { renderReportSummaryHtml } from './renderReportSummaryHtml';
import './reportSummary.css';

export function ReportSummaryDocument({ model: value, pageSize = 'A4', native = false, children }: {
  model: ReportSummaryModelV1; pageSize?: 'A4' | 'Letter'; native?: boolean; children?: React.ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [nativeLayoutError, setNativeLayoutError] = useState<string | null>(null);
  const model = useMemo(() => {
    const parsed = reportSummaryModelV1Schema.parse(value);
    if (parsed.modelHash !== hashReportSummaryModel(parsed)) throw new Error('The saved summary failed its content check.');
    return parsed;
  }, [value]);
  const html = useMemo(() => renderReportSummaryHtml(model), [model]);
  const printCss = buildNativePrintStylesheet({ pageSize: pageSize === 'Letter' ? 'letter' : 'a4', furniture: 'css',
    runningHead: `${model.identity.siteName} · Report Summary`, siteName: model.identity.siteName, pageNumberFormat: 'page-of-total',
    fontFamily: 'Newsreader, Georgia, serif', pageBackground: '#ffffff', demonstrationDisclosure: model.demonstrationDisclosure });
  useEffect(() => {
    const priorTitle = document.title;
    document.title = `${model.title} — ${model.identity.siteName}`;
    const tags = [['datacrate:summary-version', model.schemaVersion], ['datacrate:builder-version', model.builderVersion],
      ['datacrate:source-report', model.identity.reportId], ['datacrate:source-revision', model.identity.revisionId],
      ['datacrate:summary-hash', model.modelHash]].map(([name, content]) => {
      const tag = document.createElement('meta'); tag.name = name!; tag.content = content!; document.head.appendChild(tag); return tag;
    });
    return () => { document.title = priorTitle; tags.forEach(tag => tag.remove()); };
  }, [model]);
  useEffect(() => {
    if (!native) return;
    let cancelled = false;
    setNativeLayoutError(null);
    document.documentElement.setAttribute('data-native-print', 'true');
    document.documentElement.removeAttribute('data-report-ready');
    document.documentElement.removeAttribute('data-summary-layout-error');
    void (document.fonts?.ready ?? Promise.resolve()).then(async () => {
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      if (cancelled) return;
      const pages = root.current?.querySelectorAll<HTMLElement>('[data-summary-page]');
      const valid = pages?.length === 3 && [...pages].every(page => {
        const content = page.querySelector<HTMLElement>('.rs-page-content');
        return Boolean(content && content.clientHeight > 0 && content.clientWidth > 0 && content.scrollHeight <= content.clientHeight + 1
          && content.scrollWidth <= content.clientWidth + 1);
      });
      if (valid) document.documentElement.setAttribute('data-report-ready', 'true');
      else {
        const message = 'The summary exceeds its exact three-page layout.';
        document.documentElement.setAttribute('data-summary-layout-error', message);
        setNativeLayoutError(message);
      }
    }).catch(error => {
      if (cancelled) return;
      const message = `The summary could not complete its native layout: ${String(error)}`;
      document.documentElement.setAttribute('data-summary-layout-error', message);
      setNativeLayoutError(message);
    });
    return () => { cancelled = true; document.documentElement.removeAttribute('data-native-print'); document.documentElement.removeAttribute('data-report-ready'); document.documentElement.removeAttribute('data-summary-layout-error'); };
  }, [native, html, pageSize]);
  return <div ref={root} data-report-summary data-summary-native={native ? 'true' : undefined} data-summary-size={pageSize === 'Letter' ? 'letter' : 'a4'} lang="en-US">
    <style data-native-print-stylesheet>{printCss}</style>{children}
    {nativeLayoutError ? <div data-print-error role="alert">{nativeLayoutError}</div> : null}<div dangerouslySetInnerHTML={{ __html: html }} />
  </div>;
}
